import { TEST_ID_AI_PLAYER_SUMMARY } from "e2e/testId";
import { ChevronDown, ChevronUp, RefreshCw } from "lucide-react-native";
import React, { memo } from "react";
import { Platform } from "react-native";

import MobileRefreshButton from "@/features/player-detail/components/molecules/MobileRefreshButton";

import LanguageSelector, {
  type LanguageSelectorSaveDefault,
} from "@/components/molecules/LanguageSelector";

import type { LocaleOption } from "@/constants/locales";

import { Box } from "@/components/atoms/Box";
import { Pressable } from "@/components/atoms/Pressable";
import { Spinner } from "@/components/atoms/Spinner";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Text } from "@/components/atoms/Text";

/**
 * Props interface for AIPlayerSummaryView component
 */
interface AIPlayerSummaryViewProps {
  isExpanded: boolean;
  isGenerating: boolean;
  lastUpdated: string | null;
  playerName: string;
  isSmallScreen: boolean;
  /**
   * The locale the PICKER should mark, `undefined` while nothing has resolved.
   * Deliberately not `selectedLanguage`: that one always names a language
   * because generation needs one, and ticking it before the profile arrives
   * would claim a choice the user has not made (SE-6511).
   */
  pickerLocale: string | undefined;
  /** The save-as-default row, or undefined when there is no override to save. */
  saveAsDefault?: LanguageSelectorSaveDefault;
  isDark: boolean;
  contentComponent: React.ReactNode;
  onToggleExpanded: () => void;
  onLanguageChange: (language: string) => void;
  onRefresh: () => void;
  supportedLanguages: readonly LocaleOption[];
}

/**
 * Get padding classes for small vs regular screens
 * @param isSmallScreen - Small screen flag
 */
const getPaddingClasses = (isSmallScreen: boolean): string =>
  isSmallScreen ? "px-3 py-2" : "px-4 py-3";

/**
 * Get gap classes for control buttons
 * @param isSmallScreen - Small screen flag
 */
const getGapClasses = (isSmallScreen: boolean): string =>
  `items-center ${isSmallScreen ? "gap-1" : "gap-2"}`;

/**
 * Get refresh button classes
 * @param isGenerating - Generating flag
 */
const getRefreshButtonClasses = (isGenerating: boolean): string => {
  const baseClasses = "rounded-sm p-1 web:transition-all";
  const stateClasses = isGenerating
    ? "animate-pulse bg-surface-muted opacity-90"
    : "web:hover:bg-surface-muted active:bg-surface-strong";
  return `${baseClasses} ${stateClasses}`;
};

/**
 * Get content padding classes
 * @param isSmallScreen - Small screen flag
 */
const getContentPaddingClasses = (isSmallScreen: boolean): string =>
  isSmallScreen ? "p-3" : "p-4";

/**
 * Get subtitle text based on state
 * @param isGenerating - Generating flag
 * @param lastUpdated - Last updated timestamp
 */
const getSubtitleText = (
  isGenerating: boolean,
  lastUpdated: string | null
): string => {
  if (isGenerating) return "Generating summary...";
  if (lastUpdated) return `Generated ${lastUpdated}`;
  return "Tap to generate summary";
};

/** Props for the right-side controls cluster. */
interface ControlsClusterProps {
  isExpanded: boolean;
  isGenerating: boolean;
  isSmallScreen: boolean;
  pickerLocale: string | undefined;
  saveAsDefault?: LanguageSelectorSaveDefault;
  isDark: boolean;
  onToggleExpanded: () => void;
  onLanguageChange: (language: string) => void;
  onRefresh: () => void;
  supportedLanguages: readonly LocaleOption[];
}

/**
 * Renders the language selector, refresh control, and expand/collapse chevron.
 * The web and native branches diverge only on the language selector and refresh
 * affordances (native avoids the animated web button to dodge crashes).
 * @param props - Controls cluster props
 * @param props.isExpanded - Whether the summary is expanded
 * @param props.isGenerating - Whether a summary is generating
 * @param props.isSmallScreen - Whether the screen is mobile sized
 * @param props.pickerLocale - The locale the picker marks, or undefined
 * @param props.saveAsDefault - The save-as-default row, or undefined
 * @param props.isDark - Whether dark mode is active
 * @param props.onToggleExpanded - Toggle expand/collapse
 * @param props.onLanguageChange - Language change handler
 * @param props.onRefresh - Refresh handler
 * @param props.supportedLanguages - Supported language options
 */
const ControlsCluster = ({
  isExpanded,
  isGenerating,
  isSmallScreen,
  pickerLocale,
  saveAsDefault,
  isDark,
  onToggleExpanded,
  onLanguageChange,
  onRefresh,
  supportedLanguages,
}: ControlsClusterProps) => {
  const isWeb = Platform.OS === "web";
  return (
    <Stack direction="horizontal" className={getGapClasses(isSmallScreen)}>
      {/*
       * ONE picker, chosen by screen WIDTH inside the molecule. This used to
       * be a `Platform.OS === "web"` fork over two components with a
       * character-for-character identical seven-prop interface — a live
       * violation of `screen-size-not-device-type.md` (SE-6508). The molecule
       * also derives its own label from the stored locale, so the raw-code
       * fallback that rendered `ja_JP` here cannot be reintroduced by a caller.
       */}
      {supportedLanguages.length > 0 && (
        <LanguageSelector
          /*
           * The PICKER locale, not the generation locale (SE-6511). Passing
           * the latter would tick English for every user whose profile has
           * not arrived, which the molecule's `unresolved` state exists to
           * avoid.
           */
          selectedLocale={pickerLocale}
          onSelectLocale={onLanguageChange}
          options={supportedLanguages}
          size={isSmallScreen ? "sm" : "md"}
          disabled={isGenerating}
          triggerTestID={TEST_ID_AI_PLAYER_SUMMARY.LANGUAGE_SELECTOR}
          saveAsDefault={saveAsDefault}
        />
      )}

      <RefreshControlButton
        isWeb={isWeb}
        isGenerating={isGenerating}
        isSmallScreen={isSmallScreen}
        isDark={isDark}
        onRefresh={onRefresh}
      />

      <Pressable
        testID={TEST_ID_AI_PLAYER_SUMMARY.CHEVRON_BUTTON}
        onPress={onToggleExpanded}
      >
        <Icon
          as={isExpanded ? ChevronUp : ChevronDown}
          size={isSmallScreen ? "sm" : "md"}
          className={
            isWeb
              ? "text-content-secondary"
              : "text-content-secondary opacity-100 dark:opacity-70"
          }
        />
      </Pressable>
    </Stack>
  );
};

/** Props for the refresh affordance. */
interface RefreshControlButtonProps {
  isWeb: boolean;
  isGenerating: boolean;
  isSmallScreen: boolean;
  isDark: boolean;
  onRefresh: () => void;
}

/**
 * Renders the generating spinner, the web refresh button, or the native
 * crash-safe refresh button depending on platform and generation state.
 * @param props - Refresh control props
 * @param props.isWeb - Whether running on web
 * @param props.isGenerating - Whether a summary is generating
 * @param props.isSmallScreen - Whether the screen is mobile sized
 * @param props.isDark - Whether dark mode is active
 * @param props.onRefresh - Refresh handler
 */
const RefreshControlButton = ({
  isWeb,
  isGenerating,
  isSmallScreen,
  isDark,
  onRefresh,
}: RefreshControlButtonProps) => {
  if (isGenerating) {
    return (
      <Spinner
        testID={TEST_ID_AI_PLAYER_SUMMARY.GENERATING_INDICATOR}
        size="small"
      />
    );
  }
  if (!isWeb) {
    return (
      <MobileRefreshButton
        isGenerating={isGenerating}
        onRefresh={onRefresh}
        isDark={isDark}
        isSmallScreen={isSmallScreen}
      />
    );
  }
  return (
    <Pressable
      testID={TEST_ID_AI_PLAYER_SUMMARY.REFRESH_BUTTON}
      onPress={onRefresh}
      disabled={isGenerating}
      className={getRefreshButtonClasses(isGenerating)}
    >
      <Icon
        as={RefreshCw}
        size={isSmallScreen ? "sm" : "md"}
        className="text-content-secondary"
      />
    </Pressable>
  );
};

/**
 * AIPlayerSummaryView component - pure UI presentation for AI player summary
 *
 * Displays the AI-generated player summary with expand/collapse functionality,
 * language selection, and refresh capability with improved loading states.
 * The component renders content provided by the Container through the contentComponent prop.
 *
 * Language selection displays as a dropdown on web and a custom modal-based selector
 * on mobile platforms to avoid Navigation context requirement. The refresh button shows
 * a pulsing background animation during generation, and loading states persist for a
 * minimum duration to ensure visibility.
 * @param props - Component props
 * @param props.isExpanded - Whether the summary content is expanded
 * @param props.isGenerating - Whether AI summary is currently being generated
 * @param props.lastUpdated - Formatted timestamp of last summary update
 * @param props.isSmallScreen - Whether the screen is small (mobile)
 * @param props.pickerLocale - The locale the picker marks, or undefined
 * @param props.saveAsDefault - The save-as-default row, or undefined
 * @param props.isDark - Whether dark theme is active
 * @param props.contentComponent - Rendered content component from Container
 * @param props.onToggleExpanded - Callback to toggle expanded state
 * @param props.onLanguageChange - Callback when language is changed
 * @param props.onRefresh - Callback to manually refresh/regenerate summary
 * @param props.supportedLanguages - Array of supported language options
 */
const AIPlayerSummaryView: React.FC<AIPlayerSummaryViewProps> = ({
  isExpanded,
  isGenerating,
  lastUpdated,
  isSmallScreen,
  pickerLocale,
  saveAsDefault,
  isDark,
  contentComponent,
  onToggleExpanded,
  onLanguageChange,
  onRefresh,
  supportedLanguages,
}) => (
  <Box
    testID={TEST_ID_AI_PLAYER_SUMMARY.CONTAINER}
    className="border-border-outline-subtle w-full rounded-lg border bg-surface-subtle"
  >
    <Stack
      direction="horizontal"
      className={`items-center justify-between ${getPaddingClasses(isSmallScreen)}`}
    >
      {/* Left side: Title area (clickable for expand/collapse) */}
      <Pressable
        testID={TEST_ID_AI_PLAYER_SUMMARY.TITLE}
        onPress={onToggleExpanded}
        className="flex-1"
      >
        <Stack className="gap-micro">
          <Stack direction="horizontal" className="items-center gap-2">
            <Text variant="body-strong" className="text-content-primary">
              Gemini AI Player Summary
            </Text>
          </Stack>
          <Text variant="caption" className="text-content-secondary">
            {getSubtitleText(isGenerating, lastUpdated)}
          </Text>
        </Stack>
      </Pressable>

      {/* Right side: Control buttons (NOT clickable for expand/collapse) */}
      <ControlsCluster
        isExpanded={isExpanded}
        isGenerating={isGenerating}
        isSmallScreen={isSmallScreen}
        pickerLocale={pickerLocale}
        saveAsDefault={saveAsDefault}
        isDark={isDark}
        onToggleExpanded={onToggleExpanded}
        onLanguageChange={onLanguageChange}
        onRefresh={onRefresh}
        supportedLanguages={supportedLanguages}
      />
    </Stack>

    {isExpanded && (
      <Box
        className={`border-border-outline-subtle border-t ${getContentPaddingClasses(isSmallScreen)}`}
      >
        {contentComponent}
      </Box>
    )}
  </Box>
);

AIPlayerSummaryView.displayName = "AIPlayerSummaryView";

export default memo(AIPlayerSummaryView);
